// Accessibility utility functions

/**
 * Announces text to screen readers
 */
export const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Manages focus for modal dialogs and overlays
 */
export class FocusManager {
  private previousActiveElement: Element | null = null;
  private focusableElements: NodeListOf<Element> | null = null;
  private container: Element | null = null;

  constructor(container: Element) {
    this.container = container;
    this.previousActiveElement = document.activeElement;
    this.focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
  }

  trapFocus() {
    if (!this.focusableElements || this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0] as HTMLElement;
    const lastElement = this.focusableElements[this.focusableElements.length - 1] as HTMLElement;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    this.container?.addEventListener('keydown', handleKeyDown as EventListener);
    firstElement.focus();

    return () => {
      this.container?.removeEventListener('keydown', handleKeyDown as EventListener);
    };
  }

  restoreFocus() {
    if (this.previousActiveElement && 'focus' in this.previousActiveElement) {
      (this.previousActiveElement as HTMLElement).focus();
    }
  }
}

/**
 * Generates unique IDs for accessibility attributes
 */
let idCounter = 0;
export const generateId = (prefix = 'id') => {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
};

/**
 * Checks if user prefers reduced motion
 */
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Checks if user prefers high contrast
 */
export const prefersHighContrast = () => {
  return window.matchMedia('(prefers-contrast: high)').matches;
};

/**
 * Keyboard navigation helpers
 */
export const KEYBOARD_KEYS = {
  ENTER: 'Enter',
  SPACE: ' ',
  ESCAPE: 'Escape',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  HOME: 'Home',
  END: 'End',
  TAB: 'Tab',
} as const;

/**
 * Handle keyboard navigation for lists and menus
 */
export const handleArrowKeyNavigation = (
  event: KeyboardEvent,
  items: HTMLElement[],
  currentIndex: number,
  onIndexChange: (index: number) => void,
  options: {
    loop?: boolean;
    orientation?: 'horizontal' | 'vertical';
  } = {}
) => {
  const { loop = true, orientation = 'vertical' } = options;
  const { key } = event;

  let newIndex = currentIndex;

  if (orientation === 'vertical') {
    if (key === KEYBOARD_KEYS.ARROW_DOWN) {
      newIndex = currentIndex + 1;
    } else if (key === KEYBOARD_KEYS.ARROW_UP) {
      newIndex = currentIndex - 1;
    }
  } else {
    if (key === KEYBOARD_KEYS.ARROW_RIGHT) {
      newIndex = currentIndex + 1;
    } else if (key === KEYBOARD_KEYS.ARROW_LEFT) {
      newIndex = currentIndex - 1;
    }
  }

  if (key === KEYBOARD_KEYS.HOME) {
    newIndex = 0;
  } else if (key === KEYBOARD_KEYS.END) {
    newIndex = items.length - 1;
  }

  // Handle bounds
  if (newIndex < 0) {
    newIndex = loop ? items.length - 1 : 0;
  } else if (newIndex >= items.length) {
    newIndex = loop ? 0 : items.length - 1;
  }

  if (newIndex !== currentIndex) {
    event.preventDefault();
    onIndexChange(newIndex);
    items[newIndex]?.focus();
  }
};

/**
 * Format text for screen readers
 */
export const formatForScreenReader = (text: string, options: {
  includeSpelling?: boolean;
  pauseBetweenWords?: boolean;
} = {}) => {
  const { includeSpelling = false, pauseBetweenWords = false } = options;
  
  let formatted = text;
  
  if (pauseBetweenWords) {
    formatted = formatted.replace(/\s+/g, ', ');
  }
  
  if (includeSpelling) {
    formatted = formatted.split('').join(' ');
  }
  
  return formatted;
};

/**
 * Validate color contrast ratio
 */
export const getContrastRatio = (_color1: string, _color2: string): number => {
  // This is a simplified version - in production, you'd want a more robust implementation
  // For now, return a placeholder value
  return 4.5; // WCAG AA standard
};

/**
 * Check if element is visible to screen readers
 */
export const isVisibleToScreenReader = (element: Element): boolean => {
  const style = window.getComputedStyle(element);
  
  return !(
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    style.opacity === '0' ||
    element.hasAttribute('aria-hidden') ||
    element.getAttribute('aria-hidden') === 'true'
  );
};

/**
 * Get accessible name for an element
 */
export const getAccessibleName = (element: Element): string => {
  // Check aria-label first
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) return ariaLabel;
  
  // Check aria-labelledby
  const labelledBy = element.getAttribute('aria-labelledby');
  if (labelledBy) {
    const labelElement = document.getElementById(labelledBy);
    if (labelElement) return labelElement.textContent || '';
  }
  
  // Check associated label
  if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT') {
    const id = element.getAttribute('id');
    if (id) {
      const label = document.querySelector(`label[for="${id}"]`);
      if (label) return label.textContent || '';
    }
  }
  
  // Fall back to text content
  return element.textContent || '';
};